import { MultiStepLoaderModal } from "@/components/layout/multi-step-loader";
import NiceModal from "@ebay/nice-modal-react";
import type { Middleware, MutationHook } from "react-query-kit";

/**
 * Creates a middleware that shows a multi-step loader modal while a mutation is in progress.
 *
 * This middleware integrates with the React Query mutation hook to display a modal with
 * customizable loading states during mutation execution. The modal will automatically close
 * when the mutation settles (either succeeds or fails).
 *
 * @template TData - The data type returned by the mutation.
 * @template TVariables - The variables type accepted by the mutation.
 * @template TError - The error type that might be returned by the mutation.
 * @template TContext - The context type used by the mutation.
 *
 * @param options - Configuration options for the loader modal.
 * @param options.loadingStates - An array of loading states to display in sequence, each with a text property.
 *                                Defaults to ["Processing...", "Almost done..."].
 * @param options.duration - The duration in milliseconds between loading state transitions. Defaults to 500ms.
 *
 * @returns A middleware function that can be used with React Query's mutation hooks.
 */
export function showMultiStepLoaderOnMutating<
  TData,
  TVariables,
  TError,
  TContext,
>(options?: {
  loadingStates?: Array<{ text: string }>;
  duration?: number;
}): Middleware<MutationHook<TData, TVariables, TError, TContext>> {
  return (useMutationNext) => {
    return ({ onMutate, onSettled, ...restOptions }) => {
      let resolvePromise: (value: unknown) => void;
      let rejectPromise: (reason?: unknown) => void;

      return useMutationNext({
        onMutate: (variables) => {
          NiceModal.show(MultiStepLoaderModal, {
            loadingStates: options?.loadingStates ?? [
              { text: "Processing..." },
              { text: "Almost done..." },
            ],
            promise: new Promise((resolve, reject) => {
              resolvePromise = resolve;
              rejectPromise = reject;
            }),
            duration: options?.duration ?? 500,
          });

          // Call original onMutate if provided
          if (onMutate) {
            return onMutate(variables);
          }
        },
        onSettled: (data, error, variables, context) => {
          if (error) {
            rejectPromise?.(error);
          } else {
            resolvePromise?.(data);
          }

          if (onSettled) {
            return onSettled(data, error, variables, context);
          }
        },
        ...restOptions,
      });
    };
  };
}
